import { useState, useEffect, useMemo } from "react";
import Txn from "./Txn";
import { transactionAPI } from "services";
import useUniversalStore from "store/universalStore";
import {
  OpenCloseBalance,
  ListHeader,
  ShimmerLoader,
  HeaderShimmer
} from "components";
import { formatMoney, debounce } from "utils";

const TxnsListView = ({ animation, setAnimation, hideClosed, handleToggle }) => {
  const [txns, setTxns] = useState([]);
  const { accountId, isReminderChanged, write, selectedMonth, isSharedAccount } = useUniversalStore((state) => state);
  const [loading, setLoading] = useState(false);
  const [openBalance, setOpenBalance] = useState(0);


  // Filter transactions based on hideClosed toggle
  const filteredTnx = useMemo(() => {
    if (hideClosed) {
      return txns.filter((item) => !item.cancelled);
    }
    return txns;
  }, [hideClosed, txns]);


  const monthlyCash = useMemo(() => {
    let cashIn = 0;
    let cashOut = 0;

    txns.forEach(txn => {
      if (!txn.cancelled) {
        const amount = txn.amount || 0;
        if (amount > 0) cashIn += amount;
        else cashOut += Math.abs(amount);
      }
    });

    return {
      in: formatMoney(cashIn),
      out: formatMoney(cashOut),
      totalInOutChange: cashIn - cashOut
    };
  }, [txns]);


  const closeBalance = useMemo(() => {
    return openBalance + monthlyCash.totalInOutChange;
  }, [openBalance, monthlyCash]);

  const debouncedLoadAllData = useMemo(() => {
    const fetchFunc = async (mDate, accId, isSharedAcc) => {
      if (!accId || !mDate) return;
      try {
        const startDate = new Date(mDate.getFullYear(), mDate.getMonth(), 1, 0, 0, 0, 0);
        const prevMonthEnd = new Date(startDate.getTime() - 1);

        const [txnsResult, balanceResult] = await Promise.all([
          transactionAPI.getTransactionsByAccountIdWithMonth(accId, mDate),
          transactionAPI.sumTillDateByAccount(prevMonthEnd, accId)
        ]);

        // if account is shared, reverse the sign of the amount
        // const modifiedTxns = txnsResult.map((txn) => {
        //   return txn?.set("amount", isSharedAccount ? (txn?.get("amount") * -1) : txn?.get("amount"));
        // });

        const modifiedTxns = txnsResult.map((txn) => {
          return {
            id: txn.id,
            amount: isSharedAcc ? (txn.get("amount") * -1) : txn.get("amount"),
            detail: txn.get("detail"),
            date: txn.get("date"),
            type: txn.get("type"),
            cancelled: txn.get("cancelled"),
            attachment: txn.get("attachment")
          }
        })

        setTxns(modifiedTxns);
        setOpenBalance(balanceResult);
      } catch (error) {
        console.error("Error loading Khata data:", error);
      } finally {
        setLoading(false);
      }
    };

    return debounce(fetchFunc, 800);
  }, []);

  useEffect(() => {
    setLoading(true);
    debouncedLoadAllData(selectedMonth, accountId, isSharedAccount);
  }, [selectedMonth, accountId, isSharedAccount]);

  return (
    <div
      className={`row ${animation}`}
      // className={`background mb-auto position-relative z-0 d-flex flex-column animate__slideInRight`}
      onAnimationEnd={() => setAnimation("")}>
      {loading ?
        <div className="row ">
          <div className="col-6 list-group list-group-flush scrollbar-design overflow-auto px-md-3"
            style={{ height: 'calc(100vh - 225px)', maxHeight: 'calc(100vh - 225px)' }}>
            <div className="shimmer-container">
              <HeaderShimmer />
              {[...Array(6)].map((_, i) => <ShimmerLoader key={i} />)}
            </div>
          </div>
          <div className="col-6 list-group list-group-flush scrollbar-design overflow-auto px-md-3"
            style={{ height: 'calc(100vh - 225px)', maxHeight: 'calc(100vh - 225px)' }}>
            <div className="shimmer-container">
              <HeaderShimmer />
              {[...Array(6)].map((_, i) => <ShimmerLoader key={i} />)}
            </div>
          </div>
        </div>
        : <div className="row p-0 m-0">
          <div className="col-6 list-group list-group-flush scrollbar-design overflow-auto ps-4 pe-0 px-md-3"
            style={{ height: 'calc(100vh - 225px)', maxHeight: 'calc(100vh - 225px)' }}>
            <ListHeader type="in" amount={monthlyCash.in} title="Received" />
            <OpenCloseBalance type="open" sum={openBalance} />
            {filteredTnx
              ?.filter((txn) => txn.amount > 0)
              ?.map((txn, i) => (
                <Txn
                  data={txn}
                  key={txn.id || i}
                  handleToggle={handleToggle}
                  setLoading={setLoading}
                  loadTxns={() => debouncedLoadAllData(selectedMonth, accountId)}
                />
              ))}
          </div>
          <div className="col-6 list-group list-group-flush scrollbar-design overflow-auto pe-0 px-md-3"
            style={{ height: 'calc(100vh - 225px)', maxHeight: 'calc(100vh - 225px)' }}>
            <ListHeader type="out" amount={monthlyCash.out} title="Given" />
            {filteredTnx
              ?.filter((txn) => txn.amount < 0)
              ?.map((txn, i) => (
                <Txn
                  data={txn}
                  key={txn.id || i}
                  handleToggle={handleToggle}
                  setLoading={setLoading}
                  loadTxns={() => debouncedLoadAllData(selectedMonth, accountId)}
                />
              ))}
            <OpenCloseBalance type="close" sum={closeBalance} />
          </div>
        </div>}
    </div>
  );
};

export default TxnsListView;